import { useEffect, useState } from 'react'
import { LoaderCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from '@shared/ui/notification-context'
import { VerificationCodeInput } from '@features/auth'
import {
  cancelTotpSetup,
  disableTotpMfa,
  getMfaStatus,
  linkGoogleAccount,
  listExternalIdentities,
  revokeCurrentSession,
  startTotpSetup,
  unlinkGoogleAccount,
  verifyTotpSetup,
  type ExternalIdentity,
  type MfaStatusResult,
  type TotpSetupResult,
} from '@services/firebase/passwordless-auth'
import { Field, FieldLabel, LmsButton, LmsInput } from '@shared/ui'
import '@features/profile/profile-layout.css'

interface AccountSecurityPanelProps {
  readonly embedded?: boolean
}

export function AccountSecurityPanel({ embedded = false }: AccountSecurityPanelProps) {
  const { t } = useTranslation('translation')
  const { enqueueSnackbar } = useSnackbar()
  const [disableCode, setDisableCode] = useState('')
  const [firstSetupCode, setFirstSetupCode] = useState('')
  const [identities, setIdentities] = useState<readonly ExternalIdentity[]>([])
  const [isBusy, setIsBusy] = useState(false)
  const [secondSetupCode, setSecondSetupCode] = useState('')
  const [setup, setSetup] = useState<TotpSetupResult | null>(null)
  const [status, setStatus] = useState<MfaStatusResult | null>(null)

  const googleIdentity = identities.find((identity) => identity.provider === 'GOOGLE')
  const hasGoogle = Boolean(googleIdentity)

  const refreshSecurityState = async () => {
    const [nextStatus, nextIdentities] = await Promise.all([
      getMfaStatus(),
      listExternalIdentities(),
    ])
    setStatus(nextStatus)
    setIdentities(nextIdentities)
  }

  useEffect(() => {
    let isMounted = true

    void Promise.all([
      getMfaStatus(),
      listExternalIdentities(),
    ]).then(([nextStatus, nextIdentities]) => {
      if (!isMounted) {
        return
      }
      setStatus(nextStatus)
      setIdentities(nextIdentities)
    }).catch((error: unknown) => {
      enqueueSnackbar(error instanceof Error ? error.message : t('Unable to load account security.'), { variant: 'error' })
    })

    return () => {
      isMounted = false
    }
  }, [enqueueSnackbar, t])

  const runAction = async (action: () => Promise<void>, successMessage: string) => {
    try {
      setIsBusy(true)
      await action()
      enqueueSnackbar(successMessage, { variant: 'success' })
      await refreshSecurityState()
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : t('Unable to update account security.'), { variant: 'error' })
    } finally {
      setIsBusy(false)
    }
  }

  const handleStartSetup = () => runAction(async () => {
    setSetup(await startTotpSetup())
    setFirstSetupCode('')
    setSecondSetupCode('')
  }, t('Authenticator setup started.'))

  const handleVerifySetup = () => runAction(async () => {
    await verifyTotpSetup(firstSetupCode, secondSetupCode)
    setSetup(null)
    setFirstSetupCode('')
    setSecondSetupCode('')
  }, t('Authenticator MFA is enabled.'))

  const handleCancelSetup = () => runAction(async () => {
    await cancelTotpSetup()
    setSetup(null)
    setFirstSetupCode('')
    setSecondSetupCode('')
  }, t('Authenticator setup cancelled.'))

  const handleDisableMfa = () => runAction(async () => {
    await disableTotpMfa(disableCode)
    setDisableCode('')
  }, t('Authenticator MFA is disabled.'))

  const handleLinkGoogle = () => runAction(async () => {
    await linkGoogleAccount()
  }, t('Google account linked.'))

  const handleUnlinkGoogle = () => runAction(async () => {
    await unlinkGoogleAccount()
  }, t('Google account unlinked.'))

  const handleRevokeSession = () => runAction(async () => {
    await revokeCurrentSession()
  }, t('Current session was revoked.'))

  const mfaStateLabel = status
    ? status.mfaEnabled ? t('Enabled') : t('Not enabled')
    : t('Loading')

  const mfaDescription = setup
    ? t('Scan the QR code with your authenticator app, or enter the manual key.')
    : status?.mfaEnabled
      ? t('Enter your current authenticator code to disable MFA.')
      : t('Use an authenticator app to get verification codes for extra account protection.')

  const googleDescription = hasGoogle
    ? t('Linked to {{email}}.', { email: googleIdentity?.email ?? 'Google' })
    : `${t('Use your Google account to sign in faster.')} ${t('No Google account linked.')}`

  const content = (
    <section className={embedded ? 'accountSecurityPanel accountSecurityPanelEmbedded' : 'accountSecurityPanel'}>
      <div className="profileCardStack">
        <div className="profileSectionStack">
          <h2 className="profileCardTitle">{t('Account security')}</h2>
          <p className="profileMutedText">{t('Manage sign-in methods and authenticator MFA.')}</p>
        </div>

        {status?.requiresMfaEnrollment && !status.mfaEnabled ? (
          <p className="accountSecurityWarning" role="alert">
            {t('Administrator access requires authenticator MFA enrollment.')}
          </p>
        ) : null}

        <div className="accountSecurityRows">
          <div className="accountSecurityRow">
            <div className="accountSecurityRowInfo">
              <p className="profileStrongText">{t('Authenticator MFA')}</p>
              <p className="profileMutedText">{mfaDescription}</p>
              <span
                className="accountSecurityStatus"
                data-enabled={status?.mfaEnabled ? 'true' : 'false'}
              >
                {t('Status: {{state}}', { state: mfaStateLabel })}
              </span>
            </div>

            {!status ? (
              <LoaderCircle
                aria-label={t('Loading account security status')}
                className="profileSpinner"
                size={20}
                strokeWidth={2.3}
              />
            ) : null}

            {status && !status.mfaEnabled && !setup ? (
              <div className="accountSecurityRowAction">
                <LmsButton disabled={isBusy} mobileFullWidth onClick={() => { void handleStartSetup() }} variant="primary">
                  {t('Set up authenticator')}
                </LmsButton>
              </div>
            ) : null}
          </div>

          {setup ? (
            <div className="accountSecuritySetup">
              <img
                alt={t('Authenticator QR code')}
                className="accountSecurityQr"
                src={setup.qrCodeDataUrl}
              />
              <Field>
                <FieldLabel htmlFor="security-manual-key">{t('Manual key')}</FieldLabel>
                <LmsInput id="security-manual-key" readOnly value={setup.manualKey} />
              </Field>
              <p className="profileMutedText">
                {t('Enter two consecutive authenticator codes.')}
              </p>
              <VerificationCodeInput ariaLabel={t('First authenticator setup code')} disabled={isBusy} onChange={setFirstSetupCode} value={firstSetupCode} />
              <VerificationCodeInput ariaLabel={t('Second authenticator setup code')} disabled={isBusy} onChange={setSecondSetupCode} value={secondSetupCode} />
              <div className="profileActionRow">
                <LmsButton disabled={isBusy || firstSetupCode.length !== 6 || secondSetupCode.length !== 6} mobileFullWidth onClick={() => { void handleVerifySetup() }} variant="primary">
                  {t('Enable MFA')}
                </LmsButton>
                <LmsButton disabled={isBusy} mobileFullWidth onClick={() => { void handleCancelSetup() }} variant="outline">
                  {t('Cancel setup')}
                </LmsButton>
              </div>
            </div>
          ) : null}

          {status?.mfaEnabled ? (
            <div className="accountSecuritySetup">
              <VerificationCodeInput ariaLabel={t('Current authenticator code')} disabled={isBusy} onChange={setDisableCode} value={disableCode} />
              <div className="profileActionRow">
                <LmsButton disabled={isBusy || disableCode.length !== 6} mobileFullWidth onClick={() => { void handleDisableMfa() }} variant="destructive">
                  {t('Disable authenticator MFA')}
                </LmsButton>
              </div>
            </div>
          ) : null}

          <div className="accountSecurityRow">
            <div className="accountSecurityRowInfo">
              <p className="profileStrongText">{t('Google sign-in')}</p>
              <p className="profileMutedText">{googleDescription}</p>
            </div>
            <div className="accountSecurityRowAction">
              <LmsButton disabled={isBusy} mobileFullWidth onClick={() => { void (hasGoogle ? handleUnlinkGoogle() : handleLinkGoogle()) }} variant="outline">
                {hasGoogle ? t('Unlink Google') : t('Link Google')}
              </LmsButton>
            </div>
          </div>

          <div className="accountSecurityRow">
            <div className="accountSecurityRowInfo">
              <p className="profileStrongText">{t('Session')}</p>
              <p className="profileMutedText">{t('Sign out of this device immediately.')}</p>
            </div>
            <div className="accountSecurityRowAction">
              <LmsButton disabled={isBusy} mobileFullWidth onClick={() => { void handleRevokeSession() }} variant="destructive">
                {t('Revoke current session')}
              </LmsButton>
            </div>
          </div>
        </div>
      </div>
    </section>
  )

  if (embedded) {
    return content
  }

  return (
    <section className="legacyAccountContainer legacyAccountContainerMd">
      {content}
    </section>
  )
}
